import type { Issue, IssueStatus } from '../types/maintenance'

export interface NextAction {
  /** Short text for compact displays (e.g., "Review quote") */
  short: string
  /** Longer guidance text explaining what to do */
  guidance: string
  /** Color variable for styling */
  colorVar: string
  /** Whether this requires landlord action (vs waiting) */
  requiresAction: boolean
  /** Icon suggestion (lucide icon name) */
  icon: 'alert-circle' | 'clock' | 'check-circle' | 'file-text' | 'hammer' | 'x-circle'
}

export interface StatusConfig {
  label: string
  description: string
  nextAction: (issue: Issue) => NextAction | null
}

/**
 * Centralized configuration for all issue statuses and their next actions.
 * This is the single source of truth for status-related logic across the app.
 */
const ISSUE_STATUS_CONFIG: Record<IssueStatus, StatusConfig> = {
  reported: {
    label: 'Reported',
    description: 'Issue has been reported and needs acknowledgement',
    nextAction: (issue) => {
      const hasContractor = !!(issue.assigned_to || issue.assigned_approved_contractor_id)
      if (hasContractor) {
        return {
          short: 'Awaiting quote',
          guidance: 'Contractor has been assigned. Waiting for them to submit a quote.',
          colorVar: 'var(--color-primary)',
          requiresAction: false,
          icon: 'clock',
        }
      }
      return {
        short: 'Acknowledge',
        guidance: 'Review this issue and acknowledge it to proceed.',
        colorVar: 'var(--color-warning)',
        requiresAction: true,
        icon: 'alert-circle',
      }
    },
  },

  acknowledged: {
    label: 'Acknowledged',
    description: 'Issue has been acknowledged and needs a contractor',
    nextAction: (issue) => {
      const hasContractor = !!(issue.assigned_to || issue.assigned_approved_contractor_id)
      if (hasContractor) {
        return {
          short: 'Awaiting quote',
          guidance: 'Contractor has been assigned. Waiting for them to submit a quote.',
          colorVar: 'var(--color-primary)',
          requiresAction: false,
          icon: 'clock',
        }
      }
      return {
        short: 'Assign contractor',
        guidance: 'Assign a contractor to get a quote for this job.',
        colorVar: 'var(--color-warning)',
        requiresAction: true,
        icon: 'alert-circle',
      }
    },
  },

  quoted: {
    label: 'Quoted',
    description: 'Contractor has submitted a quote for review',
    nextAction: () => ({
      short: 'Review quote',
      guidance: 'Review the quote breakdown and approve to proceed with the work.',
      colorVar: 'var(--color-warning)',
      requiresAction: true,
      icon: 'file-text',
    }),
  },

  quote_approved: {
    label: 'Approved',
    description: 'Quote has been approved, waiting for contractor to schedule',
    nextAction: () => ({
      short: 'Awaiting schedule',
      guidance: 'Quote approved. Waiting for contractor to schedule the work.',
      colorVar: 'var(--color-primary)',
      requiresAction: false,
      icon: 'clock',
    }),
  },

  in_progress: {
    label: 'Scheduled',
    description: 'Contractor has scheduled the work',
    nextAction: () => ({
      short: 'Scheduled',
      guidance: 'Contractor has scheduled this job. Waiting for completion.',
      colorVar: 'var(--color-primary)',
      requiresAction: false,
      icon: 'clock',
    }),
  },

  completed: {
    label: 'Completed',
    description: 'Work is done, awaiting landlord review',
    nextAction: () => ({
      short: 'Review & close',
      guidance: 'Contractor has marked the job complete. Review the work and close the issue.',
      colorVar: 'var(--color-success)',
      requiresAction: true,
      icon: 'check-circle',
    }),
  },

  closed: {
    label: 'Closed',
    description: 'Issue has been resolved and closed',
    nextAction: () => null,
  },

  cancelled: {
    label: 'Cancelled',
    description: 'Issue has been cancelled',
    nextAction: () => ({
      short: 'Reopen',
      guidance: 'This issue was cancelled. You can reopen it if needed.',
      colorVar: 'var(--text-muted)',
      requiresAction: false,
      icon: 'x-circle',
    }),
  },
}

/**
 * Get the next action for an issue based on its current status and state.
 * Use this everywhere you need to show what action is needed.
 */
export function getNextAction(issue: Issue): NextAction | null {
  const config = ISSUE_STATUS_CONFIG[issue.status]
  if (!config) return null
  return config.nextAction(issue)
}

/**
 * Check if an issue requires landlord action.
 * Useful for filtering and counting issues that need attention.
 */
export function requiresLandlordAction(issue: Issue): boolean {
  const action = getNextAction(issue)
  return action?.requiresAction ?? false
}

